/**
 * Fire-and-forget client report of a vendor API call the browser made directly
 * (Geoapify / Mapbox / Google Maps). Never throws — metering must not break maps.
 */

import {
  IntegrationId,
  type ClientReportableIntegrationId,
} from '@ruta/shared';

import { makeApiCall } from '@/lib/api';

export async function reportIntegrationUsage(
  integrationId: ClientReportableIntegrationId,
  amount = 1,
): Promise<void> {
  if (!Number.isFinite(amount) || amount <= 0) return;
  try {
    await makeApiCall('POST', '/integrations/usage', { integrationId, amount });
  } catch (err) {
    console.warn('[integrationUsage] failed to report', integrationId, err);
  }
}

/** Convenience aliases so call sites stay readable. */
export const reportGeoapifyCredits = (credits: number) =>
  reportIntegrationUsage(IntegrationId.GEOAPIFY, credits);

export const reportMapboxApiCall = (amount = 1) =>
  reportIntegrationUsage(IntegrationId.MAPBOX, amount);

export const reportMapboxMapLoad = () =>
  reportIntegrationUsage(IntegrationId.MAPBOX_MAP_LOADS, 1);

export const reportGoogleMapsApiCall = (amount = 1) =>
  reportIntegrationUsage(IntegrationId.GOOGLE_MAPS, amount);

/** One interactive Street View panorama load (Google's Dynamic Street View SKU). */
export const reportStreetViewPanoramaLoad = () =>
  reportIntegrationUsage(IntegrationId.GOOGLE_STREET_VIEW, 1);
